import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, UserPlus, X, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { createRealtimeSocket } from '../lib/realtime';

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const NotificationBell = ({ user, collapsed }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);
  const socketRef = useRef(null);
  const isPlatformAdmin = user?.is_master_admin === true;
  const notificationBase = isPlatformAdmin ? '/platform/notifications' : '/admin-notifications';

  const fetchAll = useCallback(async () => {
    try {
      const res = await axiosInstance.get(notificationBase);
      setItems(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch {
      // Silent — bell stays in last known state
    }
  }, [notificationBase]);

  // Initial fetch + poll + socket sync
  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000);
    const onFocus = () => fetchAll();
    window.addEventListener('focus', onFocus);

    try {
      socketRef.current = createRealtimeSocket();
      socketRef.current.on('notification:new', (n) => {
        // Only show toast if relevant to this user's role
        const target = n?.role_target || [];
        if (target.includes(user?.role)) {
          toast.info(n.title || 'New notification', {
            description: n.message,
            action: n.link ? { label: 'View', onClick: () => navigate(n.link) } : undefined,
          });
          fetchAll();
        }
      });
      socketRef.current.on('user_registered', () => fetchAll());
    } catch {
      // Polling covers fallback
    }

    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      try { socketRef.current?.disconnect(); } catch { /* noop */ }
    };
  }, [fetchAll, navigate, user?.role]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axiosInstance.post(`${notificationBase}/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* noop */ }
  };

  const markAllRead = async () => {
    try {
      await axiosInstance.post(`${notificationBase}/read-all`);
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { /* noop */ }
  };

  const handleClick = (n) => {
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const iconFor = (type) => {
    if (type === 'new_user_signup') return <UserPlus size={14} className="text-sky-600" />;
    return <Bell size={14} className="text-gray-500" />;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Notifications"
        data-testid="notification-bell-btn"
        className={`relative flex items-center rounded-lg text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors ${
          collapsed ? 'p-2 justify-center w-full' : 'w-full gap-3 px-3 py-2 text-sm'
        }`}
      >
        <Bell size={18} className="flex-shrink-0" />
        {!collapsed && <span>Notifications</span>}
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className={`absolute ${collapsed ? 'top-1 right-1' : 'top-1.5 right-2'} min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center`}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className={`absolute z-50 left-full ml-2 ${collapsed ? 'bottom-0' : 'bottom-0'} w-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  data-testid="notification-mark-all-read"
                  className="text-[11px] text-sky-600 hover:text-sky-800 flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-sky-50"
                >
                  <CheckCheck size={11} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 p-0.5">
                <X size={14} />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-gray-400">
                <Bell size={24} className="mx-auto mb-2 opacity-40" />
                You&apos;re all caught up
              </div>
            ) : (
              items.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors flex gap-2.5 ${
                    !n.is_read ? 'bg-sky-50/40' : ''
                  }`}
                >
                  <div className="mt-0.5 flex-shrink-0">{iconFor(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-xs leading-tight ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {n.title}
                      </p>
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0 mt-1" />}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{formatRelative(n.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
